<!--
  AB#22077 — the before and after of one audit entry, field by field. An id
  column shows the record's words beside the number.
-->
<template>
  <div v-if="changes.length" class="ac" data-test="audit-changes">
    <div class="ac-h"><span>Field</span><span>Before</span><span>After</span></div>
    <div v-for="(c, i) in changes" :key="i" class="ac-r" :data-test="`audit-change-${c.field}`">
      <span class="ac-f">
        <span class="mono">{{ c.field }}</span>
        <small v-if="showModel">{{ modelWords(c) }}</small>
      </span>
      <span class="ac-old" :title="c.old ?? ''">{{ changeValue(c.old, c.old_label) }}</span>
      <span class="ac-new" :title="c.new ?? ''">{{ changeValue(c.new, c.new_label) }}</span>
    </div>
  </div>
  <p v-else class="ac-none" data-test="audit-changes-none">{{ emptyText }}</p>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { changeValue, type AuditChange } from '@/composables/useAuditLog';

const props = withDefaults(
  defineProps<{
    changes: AuditChange[];
    emptyText?: string;
  }>(),
  { emptyText: 'This action changed no stored fields.' }
);

/** Several records in one entry: say which row each field belongs to. */
const showModel = computed(
  () => new Set(props.changes.map((c) => `${c.model}#${c.model_id}`)).size > 1
);
const modelWords = (c: AuditChange): string =>
  `${c.model.replace(/_/g, ' ')}${c.model_id ? ` #${c.model_id}` : ''}`;
</script>

<style scoped>
.ac {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.ac-h,
.ac-r {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr);
  gap: 10px;
  padding: 6px 10px;
  font-size: 12.5px;
}
.ac-h {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  background: var(--surface-2);
}
.ac-r {
  border-top: 1px solid var(--border-soft);
  align-items: start;
}
.ac-f {
  display: flex;
  flex-direction: column;
  color: var(--ink);
}
.ac-f small {
  color: var(--ink-mute);
  font-size: 11px;
}
.mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.ac-old,
.ac-new {
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  max-height: 9em;
  overflow: auto;
}
.ac-old {
  color: var(--danger);
  text-decoration: line-through;
  text-decoration-color: color-mix(in srgb, var(--danger) 50%, transparent);
}
.ac-new {
  color: var(--success);
  font-weight: 600;
}
.ac-none {
  margin: 0;
  font-size: 12.5px;
  color: var(--ink-mute);
}
</style>
